import { Game } from 'chessboard/model/game/types';
import { Board } from 'chessboard/model/board/types';
import { Core } from 'chessboard/core/config/types';
import { StateMachine } from 'chessboard/core/state/config/types';
import { isPromotion } from 'chessboard/model/move/is-promotion';
import { ChessError } from 'chessboard/model/error';
import { ErrorCodes } from 'chessboard/model/error/types';
import { Sounds } from 'chessboard/plugins/sounds/config/types';
import { shouldDropOrDeselectPiece } from 'chessboard/core/state/model/should-drop-or-deselect-piece';
import type { Modes } from 'chessboard/modes/config/types';

type ModeWithCanDropOffBoard = Modes.API.Extended<{
  canDropOffBoard: Modes.Options.Initial.Option;
}>;

export const pieceDragging = {
  anyAPIEvent: ({
    context,
    data: { holdSelectedPiece, move, plyDiff },
    params: { draggingPiece },
    UpdateParams,
  }: StateMachine.TransitionParams) => {
    const { renderer } = context;

    if (
      draggingPiece &&
      !holdSelectedPiece &&
      shouldDropOrDeselectPiece({
        api: context.api,
        move,
        pieceSquare: draggingPiece,
        plyDiff,
      })
    ) {
      UpdateParams.resetElevatedState();

      renderer?.dropPiece(draggingPiece, context);

      return StateMachine.States.Idle;
    }

    return StateMachine.States.PieceDragging;
  },

  /**
   * If we're dragging and we receive a PointerdownRight event, then we want
   * to cancel the drag.
   */
  [Core.UserEvents.MousedownRight]: ({
    context,
    params: { draggingPiece },
    UpdateParams,
  }: StateMachine.TransitionParams) => {
    const { renderer } = context;

    if (!draggingPiece) {
      throw new ChessError({
        code: ErrorCodes.BadData,
        message:
          'Attempted to pointerdown on a dragging piece but dragging piece is null or undefined',
      });
    }

    UpdateParams.dropPiece();
    UpdateParams.deselectPiece();

    renderer?.dropPiece(draggingPiece, context);

    return StateMachine.States.Idle;
  },

  /**
   * We're dragging a piece here, so we just want to move it to the current
   * pointer coordinates.
   */
  [Core.UserEvents.Pointermove]: ({
    context,
    data: { coordsInsideBoard },
    params: { draggingPiece },
    UpdateParams,
  }: StateMachine.TransitionParams) => {
    const { renderer } = context;

    if (!draggingPiece) {
      throw new ChessError({
        code: ErrorCodes.BadData,
        message: 'Attempted to drag a piece but draggingPiece is null or undefined',
      });
    }

    UpdateParams.dragPiece(coordsInsideBoard, draggingPiece);

    renderer?.dragPiece(
      {
        coords: coordsInsideBoard,
        square: draggingPiece,
      },
      context,
    );

    return StateMachine.States.PieceDragging;
  },

  /**
   * This is an attempt to drop the piece, either on or off the board, and is
   * the most complex function in the state machine. The potential actions are:
   *
   * 1. Replace the piece if it's an illegal move.
   * 2. Open the promotion window if auto-promote is set but the user is holding
   *    the 'alt' key.
   * 3. Make a promotion move if auto-promote is set.
   * 4. Make a "null" move if we're dropping it off the board.
   * 5. Make a normal move if it's legal.
   */
  [Core.UserEvents.Pointerup]: ({
    context,
    data: { keyPressed, square },
    params: { draggingPiece, selectedPiece },
    UpdateParams,
  }: StateMachine.TransitionParams) => {
    const {
      api: { getMove, getPiece, getTurn, isCheck, move: makeMove, playSound },
      mode,
      options,
      queue,
      renderer,
    } = context;

    if (!draggingPiece) {
      throw new ChessError({
        code: ErrorCodes.BadData,
        message:
          'Attempted to pointerup on a dragging piece but dragging piece is null or undefined',
      });
    }

    if (context.options.test) {
      const el = context.el.querySelector('.dragging');
      el?.setAttribute('data-test-dragging', 'false');
      el?.removeAttribute('dragging');
    }

    if (draggingPiece === square) {
      // Is dropping piece back on the original square
      UpdateParams.dropPiece();
      renderer?.dropPiece(draggingPiece, context);

      // For the `click` and `drag` move methods deselecting a piece
      // needs to occur on pointerup rather than pointerdown since a
      // selected piece should still be draggable.
      if (
        selectedPiece === square ||
        options.moveMethod === Board.MoveMethods.Drag_Only ||
        options.moveMethod === Board.MoveMethods.DragOnly
      ) {
        // if the piece is already selected, it should be deselected
        UpdateParams.deselectPiece();

        return StateMachine.States.Idle;
      }

      UpdateParams.selectPiece(draggingPiece);

      return StateMachine.States.PieceSelected;
    }

    if (selectedPiece) {
      UpdateParams.deselectPiece();
    }

    const moveAttempt = {
      from: draggingPiece,
      to: square,
    };

    const moveOrNull = getMove(moveAttempt);

    const move =
      mode.isAllowedToMove() && moveOrNull
        ? { ...moveOrNull, animate: false, userGenerated: true, userGeneratedDrop: true }
        : false;

    // Action 2: illegal move
    if (!move) {
      UpdateParams.dropPiece();

      if (mode.handleIllegalMove) {
        queue.add(mode.handleIllegalMove, moveAttempt);
      }

      const kingSquare = isCheck();
      let kingInCheck: false | Board.SanSquare = false;

      if (getPiece(draggingPiece)?.color === getTurn() && kingSquare) {
        if (options.playSounds) {
          queue.add(playSound, Sounds.BoardSounds.Illegal);
        }

        kingInCheck = kingSquare;
      }

      renderer?.illegalMove(kingInCheck, context);
      renderer?.dropPiece(draggingPiece, context);

      if (options.moveMethod === Board.MoveMethods.DragOnly) {
        UpdateParams.deselectPiece();

        return StateMachine.States.Idle;
      }

      UpdateParams.selectPiece(draggingPiece);

      return StateMachine.States.PieceSelected;
    }

    if (isPromotion(move)) {
      // Action 3: show the promotion area
      if (!options.autoPromote || keyPressed === 'alt') {
        UpdateParams.dragPiece(<Board.Coords>{}, draggingPiece);
        UpdateParams.showPromotionArea(move);

        renderer?.dragPiece(
          {
            square: draggingPiece,
            toSquare: <Board.SanSquare>move.to,
          },
          context,
        );

        renderer?.openPromotionWindow(move, context);

        return StateMachine.States.PromotionAreaShowing;
      }

      UpdateParams.dropPiece();

      renderer?.dropPiece(move.to, context);

      queue.add(makeMove, { ...move, promotion: Game.Piece.Types.Queen });

      return StateMachine.States.Idle;
    }

    UpdateParams.dropPiece();

    if (square === null && (mode as ModeWithCanDropOffBoard).getOptions().canDropOffBoard) {
      // Action 4: drop off the board
      renderer?.removePiece(draggingPiece, context);
    } else {
      // Action 5: make a normal move
      renderer?.dropPiece(square, context);
    }

    queue.add(makeMove, move);

    return StateMachine.States.Idle;
  },
};
